import { Box } from '@mui/material';
import { ReactNode } from 'react';

import {
  TileNodeBase,
  TileNodeProps,
} from '@/components/diagrams/FlowChart/nodes/TileNode/TileNode';
import { EntityValuationActions_EntityFragment } from '@/modules/entities/details/EntityValuation/graphql/EntityValuation.generated';
import { EntityType } from '@/modules/entities/types/EntityType';
import { GroupNodeMember } from '@/modules/estateWaterfall/types';
import {
  getPresentationHoverOutlineSx,
  usePresentationRowHover,
} from '@/modules/presentation/components/PresentationHoverActions/PresentationHoverActions.hooks';
import { PresentationRowHoverToolbar } from '@/modules/presentation/components/PresentationHoverActions/PresentationRowHoverToolbar';
import { usePresentationEditingContext } from '@/modules/presentation/context/PresentationEditing.context';

import { useClientPresentationDesignerContext } from '../../ClientPresentationDesigner.context';
import {
  EditEntityButton,
  EditHoldingsButton,
  EditOrganizationButton,
  EditPersonButton,
  EditSubtrustButton,
  OpenInAppButton,
  ShowGroupDetailsButton,
  ViewSourceButton,
} from './PresentationEntityActionButtons';

/** The editable record behind a presentation diagram tile, by node kind. */
export type EntityTileTarget =
  // A real entity: edit via the entity modal, open the entity detail page.
  // `holdingsEntity` (the slim holdings slice) opts the tile into an "Edit
  // holdings" action — set by hosts that have it loaded (the ownership and
  // estate-waterfall diagrams); omitted elsewhere.
  | {
      kind: 'entity';
      entityId: string;
      entityType: EntityType;
      holdingsEntity?: EntityValuationActions_EntityFragment;
    }
  // A testamentary sub-trust (materializes at death): edit via the sub-trust
  // trowser. No detail page.
  | { kind: 'subtrust'; testamentaryEntityId: string }
  // An individual: edit via the client-profile side drawer.
  | { kind: 'individual'; clientProfileId: string }
  // A client organization: edit via the client-organization side drawer.
  | { kind: 'organization'; organizationId: string }
  // An aggregate group tile: no editable record, but hover opens the
  // "Group details" drawer listing its member entities.
  | { kind: 'group'; members: GroupNodeMember[] };

interface PresentationEditableEntityTileProps {
  /** The slot tile's own React Flow node props, rendered via `TileNodeBase`. */
  tileProps: TileNodeProps;
  /**
   * The editable record behind this tile, or null when it has no edit
   * affordance (synthetic/aggregate/placeholder nodes).
   */
  target: EntityTileTarget | null;
  /**
   * Gap between the node and its hover halo, as an `outline-offset` value (e.g.
   * `"8px"`). Paint-only, so the node never shifts. Omit for the default
   * edge-hugging inset. The estate-waterfall tiles pass a positive offset so the
   * halo reads clearly around each node.
   */
  hoverOutlineOffset?: string;
}

/**
 * The edit toolbar buttons for a tile, by target kind. A render helper (called,
 * not rendered as a `<Component />`) so its Fragment is a direct `SlideToolbar`
 * child: `SlideToolbar` flattens Fragment children and reads `startsGroup` /
 * `endsGroup` off each button to place its group divider. Wrapping the buttons
 * in a component element instead would hide them (and their group props) inside
 * that element's render output.
 *
 * "View source" trails every kind that can carry a citation. It renders nothing
 * when the record has none, so it's unconditional here — but it must stay last
 * and stay free of group props (see `ViewSourceButton`). Group tiles are the
 * exception: they aggregate several entities, so there's no single record to
 * cite.
 */
function tileToolbarButtons(
  target: EntityTileTarget,
  dismiss: () => void
): ReactNode {
  switch (target.kind) {
    case 'entity': {
      const entityTarget = {
        entityId: target.entityId,
        entityType: target.entityType,
      };
      return (
        <>
          <EditEntityButton target={entityTarget} onDismiss={dismiss} />
          {target.holdingsEntity && (
            <EditHoldingsButton
              target={entityTarget}
              entity={target.holdingsEntity}
              onDismiss={dismiss}
            />
          )}
          {/* Divide the edit actions from navigation only when "Edit holdings"
              is present, so tiles without it keep the plain two-button row. */}
          <OpenInAppButton
            startsGroup={!!target.holdingsEntity}
            target={entityTarget}
            onDismiss={dismiss}
          />
          <ViewSourceButton recordId={target.entityId} onDismiss={dismiss} />
        </>
      );
    }
    case 'subtrust':
      return (
        <>
          <EditSubtrustButton
            testamentaryEntityId={target.testamentaryEntityId}
            onDismiss={dismiss}
          />
          <ViewSourceButton
            recordId={target.testamentaryEntityId}
            onDismiss={dismiss}
          />
        </>
      );
    case 'individual':
      return (
        <>
          <EditPersonButton
            clientProfileId={target.clientProfileId}
            onDismiss={dismiss}
          />
          <ViewSourceButton
            recordId={target.clientProfileId}
            onDismiss={dismiss}
          />
        </>
      );
    case 'organization':
      return (
        <>
          <EditOrganizationButton
            organizationId={target.organizationId}
            onDismiss={dismiss}
          />
          <ViewSourceButton
            recordId={target.organizationId}
            onDismiss={dismiss}
          />
        </>
      );
    case 'group':
      return (
        <ShowGroupDetailsButton members={target.members} onDismiss={dismiss} />
      );
  }
}

/**
 * Shared presentation/design-mode diagram tile: wraps {@link TileNodeBase} with
 * the blue-hover treatment and an edit toolbar. Real entities get "Edit entity"
 * + "Open in app"; testamentary sub-trusts get "Edit subtrust"; individuals get
 * "Edit person"; client organizations get "Edit organization". The toolbar
 * actions read their handlers from {@link usePresentationEntityActionsContext}, which
 * owns the modals/trowsers OUTSIDE any diagram's remount-keyed subtree — so an
 * in-place remeasure can't unmount an open editor.
 *
 * Each diagram module supplies a thin `resolveNodeTarget` (it knows its own
 * graph); the hover/toolbar wiring lives here so both the estate-waterfall and
 * entity-diagram tiles stay in sync.
 */
export function PresentationEditableEntityTile({
  tileProps,
  target,
  hoverOutlineOffset,
}: PresentationEditableEntityTileProps) {
  // Prefer the injected editing context (e.g. the single-entity export preview);
  // fall back to the designer context so the presentation designer is unchanged.
  const editing = usePresentationEditingContext();
  const { canUserEditData } = useClientPresentationDesignerContext();
  const canEdit = editing?.canEdit ?? canUserEditData;

  // No target behind this tile, or a view-only collaborator on an edit action →
  // no hover toolbar. Group details is read-only, so it stays available to
  // view-only collaborators in design mode. The hook is still called so hook
  // order is stable; `disabled` keeps it inert. Key hover state by the React
  // Flow node id (not the entity id): one entity can appear in several death
  // sections, each its own node, so keying by node id gives every occurrence
  // its own outline and toolbar while the actions target the shared record.
  const disabled = !target || (target.kind !== 'group' && !canEdit);
  const {
    isOutlineActive,
    isToolbarActive,
    anchorRef,
    rowProps,
    toolbarProps,
    dismiss,
  } = usePresentationRowHover(tileProps.id, { disabled });

  if (!target) {
    return <TileNodeBase {...tileProps} />;
  }

  // Optionally push the hover halo outside the node (vs. the default
  // edge-hugging inset) so it reads clearly around the whole node.
  // `outline-offset` is paint-only, so the node itself doesn't shift.
  //
  // `vertical-align: top` on the wrapped tile is load-bearing: icon-variant
  // tiles render as `inline-flex`, so this block wrapper would otherwise lay
  // them out on a text baseline and reserve descender space that nudges the
  // node ~1px down. Aligning to the top removes that gap. It's a no-op for the
  // block-level (non-icon) entity tiles, so their width — and any footer that
  // wraps to it — is unchanged. (Do NOT switch the wrapper to `display: flex`:
  // that makes the tile a flex item whose width can shrink, wrapping footer
  // labels like "Includes pour-over provisions" onto an extra line.)
  const outlineSx = getPresentationHoverOutlineSx(isOutlineActive);
  const boxSx = {
    // Fill the React Flow node box so a tile with a stored height (whose only
    // height signal is the tile wrapper's `height: 100%`) resolves against the
    // node's fixed height instead of collapsing to content inside this
    // auto-height hover wrapper. Content-sized tiles keep an auto node height, so
    // `100%` resolves to auto and they still hug their content — no regression.
    height: '100%',
    '& > *': { verticalAlign: 'top' },
    ...outlineSx,
    ...(isOutlineActive && hoverOutlineOffset
      ? { outlineOffset: hoverOutlineOffset }
      : {}),
  };

  return (
    <Box
      ref={anchorRef}
      sx={boxSx}
      onMouseEnter={rowProps.onMouseEnter}
      onMouseLeave={rowProps.onMouseLeave}
    >
      <TileNodeBase {...tileProps} />
      {isToolbarActive && anchorRef.current && (
        <PresentationRowHoverToolbar
          anchorEl={anchorRef.current}
          placement="top"
          onMouseEnter={toolbarProps.onMouseEnter}
          onMouseLeave={toolbarProps.onMouseLeave}
        >
          {tileToolbarButtons(target, dismiss)}
        </PresentationRowHoverToolbar>
      )}
    </Box>
  );
}
